import { useEffect } from "react";
import { useClientProfile, useClientProfileByBooking, useClientProfileByPhone, type ClientInteraction } from "../../hooks/useClientProfile";
import { DbSection, RosterSection, UnifansSection } from "./ClientDossier";
import { isMirKvSource } from "../../utils/mirkv";
/* 05.09 (владелец): импорт напрямую (не через shared/index) — PlayersBadge сам импортирует эту карточку;
   цикл безопасен: обе — function-декларации, друг друга зовут только в рендере. */
import { PlayersBadge } from "../shared/PlayersBadge";
import { CommentLog } from "./CommentLog";
import { historyCount } from "./clientCardModel";
import { originRows } from "./clientOriginModel";
import css from "./ClientInfoCard.module.css";

export interface ClientInfoCardProps {
  /** Бронь, по которой тянем клиента (edit). */
  bookingId?: number;
  /** Либо клиент напрямую — live-находка по номеру в create (15.08). */
  clientId?: number | null;
  /** Либо только номер: клиента в clients нет, профиль по броням номера. */
  phone?: string;
  /** Имя клиента из формы — показываем сразу, пока грузится профиль. */
  fallbackName?: string;
  onClose: () => void;
}

function fmtDate(iso?: string | null): string {
  if (!iso) return "";
  const d = iso.slice(0, 10).split("-");
  return d.length === 3 ? `${d[2]}.${d[1]}.${d[0]}` : iso;
}
const rub = (n: unknown) => `${Number(n || 0).toLocaleString("ru-RU")} ₽`;
/** source игр Квестгида без сумм (apps/api/src/utils/kgGames.ts): отчёта нет, но это сыгранная игра. */
const KG_GAME_SOURCE = "Квестгид · архив";
/** PII-плейсхолдер нерасшифрованного V1-импорта — не значение, скрываем. */
const pii = (v?: string | null) => { const t = (v ?? "").trim(); return t === "[encrypted]" ? "" : t; };
/** Локальная (не UTC) дата YYYY-MM-DD — бейдж «Будущая» у ещё не сыгранных броней. */
const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

/**
 * «Инфо» карточка клиента поверх модалки редактирования брони. Показывает:
 * кто клиент, когда был, во что играл, сколько заплатил и что о нём написали
 * операторы в отчётах. Данные — getClientProfile по id брони.
 */
export function ClientInfoCard({ bookingId, clientId, phone: phoneProp, fallbackName, onClose }: ClientInfoCardProps) {
  const byBooking = useClientProfileByBooking(bookingId ?? null);
  const byId = useClientProfile(bookingId ? null : clientId ?? null);
  const byPhone = useClientProfileByPhone(!bookingId && clientId == null ? phoneProp ?? null : null);
  const { profile, loading, error } = bookingId ? byBooking : clientId != null ? byId : byPhone;

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);

  const name = pii(profile?.name) || pii(profile?.current?.client_name) || pii(fallbackName) || "Клиент";
  const phone = pii(profile?.phone);
  const tg = pii(profile?.telegram);
  const cur = profile?.current;
  /* id === null — бронь есть, но карточки клиента в базе нет. С 23.08 API в этом случае
     собирает профиль по телефону брони (история, счётчики, досье) — его и показываем.
     07.09 (владелец, «Пришли сегодня»): «истории нет» — только если по телефону тоже пусто. */
  const hist = profile?.history ?? [];
  const noClient = !!profile && profile.id == null && hist.length === 0 && !(profile.team_history ?? []).length && !Number(profile.bookings_count) && !Number(profile.leads_count);
  const byPhoneOnly = !!profile && profile.id == null && !noClient;
  /* «Всего игр» = завершённые с отчётом (Б2) — визиты больше не выдаются за игры. */
  const games = Number(profile?.games_played ?? 0);
  const spent = Number(profile?.total_spent ?? 0);
  const pending = Number(profile?.pending_amount ?? 0);
  const avg = games > 0 ? Math.round(spent / games) : 0;
  /* 30.09: игры в чужих командах (по вейверам) — в общей ленте, свежие первыми */
  const team = profile?.team_history ?? [];
  const history = !team.length ? hist : [...hist, ...team].sort((a, b) => `${b.date} ${b.custom_time || b.time || ""}`.localeCompare(`${a.date} ${a.custom_time || a.time || ""}`));
  /* 27.10: «История N» — все записи, а не подгруженные строки (лента урезана: 40 своих + 20 в чужих командах) */
  const histN = historyCount(profile, history.length);
  /* Плотная строка счётчиков; нули не показываем (A39 — без прочерков и шума). */
  const visits = profile ? [
    Number(profile.bookings_count) > 0 ? `броней ${profile.bookings_count}` : "",
    Number(profile.leads_count) > 0 ? `лидов ${profile.leads_count}` : "",
    Number(profile.cancellations_count) > 0 ? `отмен ${profile.cancellations_count}` : "",
    Number(profile.team_games_count) > 0 ? `в чужих командах ${profile.team_games_count}` : "",
  ].filter(Boolean).join(" · ") : "";

  return (
    <div className={css.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={css.card} role="dialog" aria-modal="true" aria-label={`Карточка клиента ${name}`}>
        <div className={css.head}>
          <div className={css.hName} title={name}>{name}</div>
          <button type="button" className={css.close} onClick={onClose} aria-label="Закрыть">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M2 2 L12 12 M12 2 L2 12" /></svg>
          </button>
        </div>

        <div className={css.body}>
          {loading && !profile ? (
            <div className={css.msg}>Загрузка профиля…</div>
          ) : error ? (
            <div className={css.msg}>Ошибка: {error}</div>
          ) : !profile ? (
            <div className={css.msg}>Этот клиент ещё не в базе — истории пока нет.</div>
          ) : (
            <>
              <div className={css.blockHdr}>1 · Юни-букинг</div>
              {cur ? (
                <div className={css.cur}>
                  <div className={css.vHead}>
                    <span className={css.curKind}>Эта бронь</span>
                    <span className={css.vWhen}>{fmtDate(cur.date)}{(cur.custom_time || cur.time) ? ` · ${String(cur.custom_time || cur.time).slice(0, 5)}` : ""}</span>
                    {cur.short_name ? <span className={css.vQuest}>{cur.short_name}</span> : null}
                  </div>
                  <div className={css.vMeta}>
                    {/* 05.09 (владелец): бейдж-индикатор — карточка уже открыта, вложенный клик не нужен */}
                    {cur.players ? <PlayersBadge count={cur.players} size="sm" /> : null}
                    {cur.source ? <span className={css.vSrc}>{cur.source}</span> : null}
                  </div>
                  {cur.comment ? <div className={css.curComm}>ДК: {cur.comment}</div> : null}
                  <CommentLog log={cur.comment_log} />
                </div>
              ) : null}
              {noClient ? <div className={css.msg}>Клиент этой брони в базе не найден — истории нет.</div> : null}
              {byPhoneOnly ? <div className={css.msg}>Карточки клиента в базе нет — история собрана по телефону брони.</div> : null}
              {noClient ? null : (
              <div className={css.summary}>
                {phone ? <Row k="Телефон" v={<a className={css.link} href={`tel:${phone.replace(/[^\d+]/g, "")}`}>{phone}</a>} /> : null}
                {tg ? <Row k="Telegram" v={<span className={css.accent}>{tg}</span>} /> : null}
                <Row k="Всего игр" v={<b>{games}</b>} />
                {visits ? <Row k="Визиты" v={visits} /> : null}
                {/* 27.10 (Квестгид): «С нами с» и «Бронирует за ~N дн.» — только когда данные есть */}
                {originRows(profile.origin).map((r) => <Row key={r.k} k={r.k} v={<span title={r.hint || undefined}>{r.v}</span>} />)}
                <Row k="Потратил" v={<span className={css.gold}>{rub(spent)}</span>} />
                {pending > 0 ? <Row k="Ожидается" v={<span className={css.pending}>{rub(pending)}</span>} /> : null}
                {avg > 0 ? <Row k="Средний чек" v={<span className={css.gold}>{rub(avg)}</span>} /> : null}
                {profile.favorite_quest ? <Row k="Любимый квест" v={profile.favorite_quest} /> : null}
                {profile.note ? <Row k="Заметка" v={profile.note} /> : null}
              </div>
              )}

              {noClient ? null : (
              <>
              <div className={css.secLbl} title={histN > history.length ? `Показаны последние ${history.length} из ${histN}` : undefined}>История {histN ? <b>{histN}</b> : null}</div>
              {history.length === 0 ? (
                <div className={css.msg}>Визитов пока не было.</div>
              ) : (
                history.map((ix) => <Visit key={ix.id} ix={ix} />)
              )}
              <RosterSection roster={profile.roster ?? []} />
              </>
              )}
              <DbSection team={profile.team ?? []} bookingTgs={profile.booking_telegrams ?? []} />
              <UnifansSection team={profile.team ?? []} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className={css.row}>
      <span className={css.rowK}>{k}</span>
      <span className={css.rowV}>{v}</span>
    </div>
  );
}

function Visit({ ix }: { ix: ClientInteraction }) {
  /* Бейдж статуса (Б2): Отмена/Неявка > В команде (30.09, по вейверу чужой брони) > Игра (есть отчёт) > Лид > Будущая > Бронь. */
  const cancelled = ix.status === "cancelled";
  const noShow = ix.status === "no_show";
  const team = ix.via === "team";
  const isGame = ix.report_total != null || !!ix.operator_id || (ix.status === "completed" && ix.source === KG_GAME_SOURCE);   // 27.10: игра Квестгида без сумм
  const isLead = ix.type === "lead";
  const future = !cancelled && !noShow && !isGame && !isLead && ix.date > todayIso();
  const kind = cancelled ? "Отмена" : noShow ? "Неявка" : team ? "В команде" : isGame ? "Игра" : isLead ? "Лид" : future ? "Будущая" : "Бронь";
  const kindCls = cancelled || noShow ? css.vCancel : isGame || team ? css.vGame : isLead ? css.vLead : css.vBook;
  /* Только итог сыгранного; предоплаты в досье не нужны (20.08, Саша). */
  const money = team ? 0 : ix.report_total ?? 0;   // чек чужой команды — не деньги этого клиента
  const breakdown = team ? "" : [
    ix.quest_price ? `квест ${rub(ix.quest_price)}` : "",
    ix.tea_price ? `чай ${rub(ix.tea_price)}` : "",
    ix.mc_price ? `МК ${rub(ix.mc_price)}` : "",
  ].filter(Boolean).join(" · ");

  return (
    <div className={`${css.visit} ${kindCls} ${isMirKvSource(ix.source) ? css.mirkv : ""}`}>
      <div className={css.vHead}>
        <span className={css.vKind}>{kind}</span>
        <span className={css.vWhen}>{fmtDate(ix.date)}{(ix.custom_time || ix.time) ? ` · ${String(ix.custom_time || ix.time).slice(0, 5)}` : ""}</span>
        {ix.short_name ? <span className={css.vQuest}>{ix.short_name}</span> : null}
      </div>
      <div className={css.vMeta}>
        {ix.players ? <PlayersBadge count={ix.players} size="sm" /> : null}
        {Number(money) > 0 ? <span className={css.vSum}>итог {rub(money)}</span> : null}
        {ix.operator_name ? <span className={css.vOp}>оператор {ix.operator_name}</span> : null}
        {ix.source ? <span className={css.vSrc}>{ix.source}</span> : null}
      </div>
      {breakdown ? <div className={css.vLine}>{breakdown}</div> : null}
      {/* Что написал оператор в отчёте — главное по задаче. */}
      {ix.report_activity ? <div className={css.vLine}>Активность: <b>{ix.report_activity}</b></div> : null}
      {ix.report_comment ? <div className={css.vNote}>“{ix.report_comment}”</div> : null}
      {ix.booking_comment ? <div className={css.vNoteDim}>ДК: {ix.booking_comment}</div> : null}
    </div>
  );
}
